import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { filterToValidAttributes } from '@zocdoc/dom-property';

const __BROWSER__ = typeof document !== 'undefined';

/**
    `Portal` is used to render content to places like the HTML `<body>` tag, avoiding style leakage and parent
    layout contexts. Only accepts a single top-level child; naked text, etc will be wrapped in a `<div>`.
 */
class Portal extends Component {
    static propTypes = {
        /**
         * any [React-supported attribute](https://facebook.github.io/react/docs/tags-and-attributes.html#html-attributes)
         */
        '*': PropTypes.any,

        // single child only - arrays not allowed

        /**
         * any normal React child, but must be singular; multiple sibling children must have a common wrapper,
         * such as a "layout" `<div>`
         * ✅ OK:
         * ```jsx
         * <Portal>
         *   foo
         * </Portal>
         * <Portal>
         *   <div>foo</div>
         * </Portal>
         * <Portal>
         *   <div>
         *       <div>foo</div>
         *       <div>bar</div>
         *   </div>
         * </Portal>
         * ```
         * ⛔️ Not OK:
         * ```jsx
         * <Portal>
         *   <div>foo</div>
         *   <div>bar</div>
         * </Portal>
         * ```
         */
        children: PropTypes.node,

        /**
         * the location to append the generated portal and child elements
         */
        destination: __BROWSER__
            ? PropTypes.instanceOf(window.HTMLElement)
            : PropTypes.shape({ nodeType: PropTypes.number }),

        // escape hatch for testing etc, to render inline instead of another node
        inline: PropTypes.bool,

        /**
         * the ID used to link the portal origin to the destination; added to generated `<div>` appended
         * to the destination HTML node. This should be deterministic such as to not defeat SSR.
         */
        portalId: PropTypes.string.isRequired,
    }

    static defaultProps = {
        children: null,
        destination: __BROWSER__ ? document.body : null,
        inline: false,
    }

    static PORTAL_DATA_ATTRIBUTE = 'data-portal-id'

    // the <div> that the children are rendered into
    $portal = null

    constructor(props) {
        super(props);

        if (__BROWSER__) {
            this.$portal = document.createElement('div');
            this.props.destination.appendChild(this.$portal);
        }
    }

    componentDidMount() { this.setPortalId(); }
    componentDidUpdate() { this.setPortalId(); }

    componentWillUnmount() {
        this.props.destination.removeChild(this.$portal);
    }

    render() {
        return (
            <span
                {...filterToValidAttributes(this.props)}
                {...{ [Portal.PORTAL_DATA_ATTRIBUTE]: this.props.portalId }}
            >
                {this.props.inline ? this.props.children : this.maybeRenderPortal()}
            </span>
        );
    }

    maybeRenderPortal() {
        if (__BROWSER__) {
            return createPortal(this.props.children, this.$portal);
        }
    }

    setPortalId() {
        if (this.$portal) {
            this.$portal.id = this.props.portalId || this.id;
        }
    }
}

export {
    Portal as default,
    Portal,
};
